import { createSelector } from "@ngrx/store";
import { displayPages } from "@hrra/core";
import { HRRAExceptionCode } from "@hrra/common-models";
import { selectCurrentWebsiteNameForSeo, selectCurrentWebsiteShortName, selectGeneralDefaultNumberOfListItemsOnPage } from "../../selectors";
import { selectJobseekerStoreState } from "../jobseeker-profile/selectors";
import { JobseekerInviteStatsViewModel } from "src/app/jobseeker/shared/models/jobseeker-invite-model";
import { JobseekerInviteFilterEnum } from "src/app/jobseeker/shared/models/enums/invite-list-filter-enum";
import { announcementDeadlineTodayHelper } from "src/app/shared/helpers/announcement-publish-date-helper";
import { MetaDataModel, OpenGraphModel } from "src/app/shared/models/meta-model";

function inviteCountForFilter(stats: JobseekerInviteStatsViewModel, filter: JobseekerInviteFilterEnum): number {
    if (!stats) return 0;

    switch (filter) {
        case JobseekerInviteFilterEnum.New: return stats.new;
        case JobseekerInviteFilterEnum.InterestedAndApplied: return stats.interestedAndApplied;
        case JobseekerInviteFilterEnum.Interested: return stats.interested;
        case JobseekerInviteFilterEnum.NotInterested: return stats.notInterested;
        case JobseekerInviteFilterEnum.All:
        default: return stats.all;
    }
}

export const selectJobseekerVacancyApplications = createSelector(
    selectJobseekerStoreState,
    s => s.applies.vacancyApplications.applicationModel?.applications
);

export const selectJobseekerCvBoxApplications = createSelector(
    selectJobseekerStoreState,
    s => s.applies.cvBoxApplications.applicationModel?.applications
);

export const selectAnnouncementAppliesCurrentPage = createSelector(
    selectJobseekerStoreState,
    s => s.applies.vacancyApplications.pageState.currentPage
);

export const selectCvBoxAppliesCurrentPage = createSelector(
    selectJobseekerStoreState,
    s => s.applies.cvBoxApplications.pageState.currentPage
);

export const selectVacancyApplicationsPageCount = createSelector(
    selectJobseekerStoreState,
    selectGeneralDefaultNumberOfListItemsOnPage,
    (s, p) => {
    let result = Math.max(
      1,
      Math.ceil(s.applies.vacancyApplications.applicationModel?.totalRecords / p)
    );
  
    return result;
});

export const selectCvBoxApplicationsPageCount = createSelector(
    selectJobseekerStoreState,
    selectGeneralDefaultNumberOfListItemsOnPage,
    (s, p) => {
    if(s.applies.cvBoxApplications.applicationModel == null || p == null) return 1;

    let result = Math.max(
      1,
      Math.ceil(s.applies.cvBoxApplications.applicationModel.totalRecords / p)
    );
  
    return result;
});

export const selectVacancyApplicationsDisplayPages = createSelector(
    selectVacancyApplicationsPageCount,
    selectJobseekerStoreState,
    (c, s) => {
        let current = s.applies.vacancyApplications.pageState.currentPage;
        let pageLinkCount = s.applies.vacancyApplications.pageState.pageLinkCount;
        let pageCount = c;

        return displayPages(current, pageLinkCount, pageCount);
});

export const selectCvBoxApplicationsDisplayPages = createSelector(
    selectCvBoxApplicationsPageCount,
    selectJobseekerStoreState,
    (c, s) => {
        let current = s.applies.cvBoxApplications.pageState.currentPage;
        let pageLinkCount = s.applies.cvBoxApplications.pageState.pageLinkCount;
        let pageCount = c;

        return displayPages(current, pageLinkCount, pageCount);
});

export const selectVacancyApplicationIsLoading = createSelector(
    selectJobseekerStoreState,
    s => s.applies.vacancyApplications.loading
);

export const selectCvBoxApplicationIsLoading = createSelector(
    selectJobseekerStoreState,
    s => s.applies.cvBoxApplications.loading
);


// ----- Invites -----

const selectInvitesState = createSelector(
    selectJobseekerStoreState,
    s => s.applies.invites
);

export const selectInviteItems = createSelector(
    selectInvitesState,
    s => {
        if (!s.items) return s.items;

        const today = new Date();
        return s.items.map(item => ({
            ...item,
            isDeadlineToday: !!item.deadline && announcementDeadlineTodayHelper(item.deadline, today)
        }));
    }
);

export const selectInviteFilter = createSelector(
    selectInvitesState,
    s => s.filter
);

export const selectInviteStats = createSelector(
    selectInvitesState,
    s => s.stats
);

export const selectInvitesCurrentPage = createSelector(
    selectInvitesState,
    s => s.pageState.currentPage
);

export const selectInvitesPageCount = createSelector(
    selectInvitesState,
    selectGeneralDefaultNumberOfListItemsOnPage,
    (s, pageSize) => {
        if (pageSize == null) return 1;
        return Math.max(1, Math.ceil(inviteCountForFilter(s.stats, s.filter) / pageSize));
    }
);

export const selectInvitesDisplayPages = createSelector(
    selectInvitesPageCount,
    selectInvitesState,
    (pageCount, s) => displayPages(s.pageState.currentPage, s.pageState.pageLinkCount, pageCount)
);

export const selectInvitesIsLoading = createSelector(
    selectInvitesState,
    s => s.loading
);


// ----- Invite details -----

const selectInviteDetailsState = createSelector(
    selectJobseekerStoreState,
    s => s.applies.inviteDetails
);

export const selectInviteAnnouncementDetails = createSelector(
    selectInviteDetailsState,
    s => s.announcement
);

export const selectAnnouncementInvitedJobseeker = createSelector(
    selectInviteDetailsState,
    s => s.announcementInvitedJobseeker
);

export const selectAnnouncementInvitedJobseekerMetaData = createSelector(
  selectInviteDetailsState,
  selectCurrentWebsiteNameForSeo,
  selectCurrentWebsiteShortName,
  (s, w, wn) => {
    const existingMetadata = s.metaData;
    if (existingMetadata == null) return null;

    const metadata: MetaDataModel = <MetaDataModel>{};
    const og: OpenGraphModel = <OpenGraphModel>{};

    og.description = existingMetadata.openGraphData.description;
    og.isAvailable = existingMetadata.openGraphData.isAvailable;
    og.title = existingMetadata.openGraphData.title;
    og.type = existingMetadata.openGraphData.type;
    og.image = '/assets/img/local/social-media/' + wn + '.png';

    metadata.openGraphData = og;
    metadata.seoData = existingMetadata.seoData;

    return {
      website: w,
      metaData: metadata,
    };
  }
);

export const selectAnnouncementInvitedJobseekerId = createSelector(
    selectInviteDetailsState,
    s => s.announcementInvitedJobseeker?.announcementInvitedJobseekerId
);

export const selectIsJobseekerInvited = createSelector(
    selectInviteDetailsState,
    s => s.isJobseekerInvited
);

export const selectInviteAnnouncementNotFound = createSelector(
    selectInviteDetailsState,
    s => s.announcementFailureDetails?.errorCode == HRRAExceptionCode.AnnouncementNotFound
);

export const selectInviteAnnouncementIsLoading = createSelector(
    selectInviteDetailsState,
    s => s.loading
);

export const selectInviteAnnouncementPublishDate = createSelector(
    selectInviteAnnouncementDetails,
    e => e?.renewalDate == null ? e?.publishDate : e?.renewalDate
);

export const selectInviteResponding = createSelector(
    selectInviteDetailsState,
    s => s.responding
);